// God → Parser: the live rules that turn bank alerts into credits. Edits go live within
// seconds (no redeploy); the test tool dry-runs the unsaved rules on the page.
import { useCallback, useEffect, useState } from "react";
import { Code2, FlaskConical, Regex, RotateCcw, Save, Smartphone, Type } from "lucide-react";
import { toast } from "sonner";
import { god } from "../godApi";
import {
  Button, Callout, Code, Dialog, Field, Input, Page, Pill, Rows, Section, Skeleton, Textarea, ago, inr, when,
} from "../kit";

const listToText = (a) => (Array.isArray(a) ? a.join("\n") : "");
const textToList = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);
const labelsToText = (o) => Object.entries(o || {}).map(([k, v]) => `${k} = ${v}`).join("\n");
function textToLabels(t) {
  const out = {};
  (t || "").split("\n").forEach((line) => {
    const s = line.trim();
    if (!s) return;
    const i = s.indexOf("=");
    if (i === -1) return;
    const k = s.slice(0, i).trim();
    const v = s.slice(i + 1).trim();
    if (k && v) out[k] = v;
  });
  return out;
}

const toFields = (cfg) => ({
  packages: labelsToText(cfg.package_labels),
  capture: listToText(cfg.capture_packages),
  credit: listToText(cfg.credit_words),
  strong: listToText(cfg.strong_credit_words),
  debit: listToText(cfg.debit_words),
  reject: listToText(cfg.reject_words),
  balance: listToText(cfg.balance_hints),
  adjacency: String(cfg.adjacency ?? 40),
  amount_regex: cfg.amount_regex || "",
  rrn_regex: cfg.rrn_regex || "",
  vpa_regex: cfg.vpa_regex || "",
});

const build = (f) => ({
  package_labels: textToLabels(f.packages),
  capture_packages: textToList(f.capture),
  credit_words: textToList(f.credit),
  strong_credit_words: textToList(f.strong),
  debit_words: textToList(f.debit),
  reject_words: textToList(f.reject),
  balance_hints: textToList(f.balance),
  adjacency: Number(f.adjacency) || 0,
  amount_regex: f.amount_regex,
  rrn_regex: f.rrn_regex,
  vpa_regex: f.vpa_regex,
});

const lines = (t) => textToList(t).length;

export default function Parser() {
  const [f, setF] = useState(null);       // editable fields
  const [saved, setSaved] = useState(""); // JSON of the live rules, for "unsaved changes"
  const [meta, setMeta] = useState({});   // version/updated_at/by + parser_version
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [test, setTest] = useState({ package_name: "com.phonepe.app", title: "PhonePe", body: "" });
  const [testOut, setTestOut] = useState(null);
  const [testing, setTesting] = useState(false);

  const hydrate = (cfg) => { const x = toFields(cfg); setF(x); setSaved(JSON.stringify(build(x))); };

  const load = useCallback(async () => {
    try {
      const r = await god.parserConfig();
      const x = toFields(r.config);
      setF(x); setSaved(JSON.stringify(build(x)));
      setMeta({ version: r.config.version, updated_at: r.config.updated_at, updated_by: r.config.updated_by, parser_version: r.parser_version });
      setErr("");
    } catch (e) { setErr(e.message); toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!f) return <Page>{err ? <Callout tone="red" title="Couldn't load parser rules">{err}</Callout> : <Section><Skeleton rows={8} /></Section>}</Page>;

  const dirty = JSON.stringify(build(f)) !== saved;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const r = await god.saveParserConfig(build(f));
      setMeta({ ...meta, version: r.config.version, updated_at: r.config.updated_at, updated_by: r.config.updated_by });
      setSaved(JSON.stringify(build(f)));
      toast.success(`Saved — v${r.config.version} is now live (no restart).`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const r = await god.resetParserConfig();
      hydrate(r.config);
      setMeta({ ...meta, version: r.config.version });
      setConfirmReset(false);
      toast.success(`Reset to defaults — v${r.config.version} live.`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const reload = async () => {
    setBusy(true);
    await load();
    setBusy(false);
  };

  const runTest = async (e) => {
    e?.preventDefault();
    setTestOut(null); setTesting(true);
    try { setTestOut(await god.testParserConfig({ ...test, config: build(f) })); }
    catch (x) { setTestOut({ error: x.message }); }
    finally { setTesting(false); }
  };

  const area = (label, k, hint) => (
    <Field label={`${label} · ${lines(f[k])}`} hint={hint}>
      <Textarea mono value={f[k]} onChange={set(k)} spellCheck={false} rows={7} />
    </Field>
  );

  const p = testOut?.parsed || {};

  return (
    <Page>
      <Section title="Live rules" sub="Edits go live within seconds — no redeploy." icon={Code2}
        actions={<>
          {dirty ? <Pill tone="amber" dot>Unsaved changes</Pill> : <Pill tone="green" dot>Live</Pill>}
          <Button size="sm" onClick={reload} disabled={busy}>Reload</Button>
          <Button size="sm" variant="danger" icon={RotateCcw} onClick={() => setConfirmReset(true)} disabled={busy}>Reset to defaults</Button>
          <Button size="sm" variant="primary" icon={Save} onClick={save} disabled={busy || !dirty}>{busy ? "Saving…" : "Save & go live"}</Button>
        </>}>
        <Rows items={[
          ["Parser schema", meta.parser_version, { mono: true }],
          ["Config version", meta.version != null ? `v${meta.version}` : null, { mono: true }],
          ["Last edit", meta.updated_at ? `${when(meta.updated_at)} · ${ago(meta.updated_at)}` : null],
          ["Edited by", meta.updated_by],
        ]} />
      </Section>

      <Section title="Test a notification" sub="Dry-runs the rules on this page, including unsaved edits. Nothing is saved." icon={FlaskConical}>
        <form onSubmit={runTest} className="pr-test">
          <div className="k-form k-form-3">
            <Field label="Package">
              <Input mono value={test.package_name} onChange={(e) => setTest({ ...test, package_name: e.target.value })} placeholder="package name" />
            </Field>
            <Field label="Title">
              <Input value={test.title} onChange={(e) => setTest({ ...test, title: e.target.value })} placeholder="title" />
            </Field>
            <Field label="Body">
              <Input value={test.body} onChange={(e) => setTest({ ...test, body: e.target.value })} placeholder="e.g. Rs.500.37 credited by john@oksbi" />
            </Field>
          </div>
          <div className="k-actions">
            <button type="submit" className="k-btn k-btn-primary" disabled={testing}><FlaskConical size={16} strokeWidth={2.1} />{testing ? "Testing…" : "Test"}</button>
          </div>
        </form>
        {testOut && (
          <div className="pr-result">
            {testOut.error ? (
              <Callout tone="red" title="Test failed">{testOut.error}</Callout>
            ) : testOut.matched ? (
              <>
                <Callout tone="green" title={`Credit — ${inr((p.amount_paisa || 0) / 100)}`}>This would be treated as money in.</Callout>
                <Rows items={[
                  ["Amount", inr((p.amount_paisa || 0) / 100)],
                  ["Bank", p.bank_label],
                  ["Payer VPA", p.payer_vpa, { mono: true }],
                  ["RRN", p.rrn, { mono: true }],
                ]} />
                <Code value={p} />
              </>
            ) : (
              <Callout tone="amber" title="Not a credit">This notification would be ignored.</Callout>
            )}
          </div>
        )}
      </Section>

      <Section title="Signal words" sub="One entry per line. Matching is case-insensitive." icon={Type}>
        <div className="k-form k-form-3">
          {area("Credit words", "credit", "Money-in signal, one per line.")}
          {area("Strong credit phrases", "strong", "Bypass the debit veto.")}
          {area("Debit words", "debit", "Veto a credit.")}
          {area("Reject words", "reject", "Never a settled credit.")}
          {area("Balance hints", "balance", "Skip balance figures.")}
        </div>
      </Section>

      <Section title="Apps & capture" icon={Smartphone}>
        <div className="k-form">
          {area("Monitored packages", "packages", "One per line: com.bank.app = Label")}
          <div className="pr-col">
            {area("Capture allowlist", "capture", "The app forwards these. Blank = all monitored; * = every app.")}
            <Field label="Adjacency (chars)">
              <Input value={f.adjacency} onChange={set("adjacency")} inputMode="numeric" />
            </Field>
          </div>
        </div>
      </Section>

      <Section title="Extraction patterns" sub="Regular expressions applied to the notification text." icon={Regex}>
        <div className="k-form k-form-3">
          <Field label="Amount regex"><Input mono value={f.amount_regex} onChange={set("amount_regex")} spellCheck={false} /></Field>
          <Field label="RRN regex"><Input mono value={f.rrn_regex} onChange={set("rrn_regex")} spellCheck={false} /></Field>
          <Field label="Payer VPA regex"><Input mono value={f.vpa_regex} onChange={set("vpa_regex")} spellCheck={false} /></Field>
        </div>
      </Section>

      <div className="k-actions">
        {dirty && <Button onClick={() => setF(toFieldsFromSaved(saved))} disabled={busy}>Discard changes</Button>}
        <Button variant="primary" icon={Save} onClick={save} disabled={busy || !dirty}>{busy ? "Saving…" : "Save & go live"}</Button>
      </div>

      {confirmReset && (
        <Dialog title="Reset all rules to defaults?" onClose={() => !busy && setConfirmReset(false)}
          footer={<>
            <Button onClick={() => setConfirmReset(false)} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={reset} disabled={busy}>{busy ? "Resetting…" : "Reset to defaults"}</Button>
          </>}>
          <p className="pr-p">Every parsing rule goes back to the built-in defaults. This goes live immediately and replaces any edits.</p>
        </Dialog>
      )}

      <style>{`
        .pr-test { display: flex; flex-direction: column; gap: 14px; }
        .pr-result { display: flex; flex-direction: column; gap: 12px; margin-top: 14px; }
        .pr-col { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
        .pr-col .k-textarea { min-height: 96px; }
        .pr-p { margin: 0; font-size: 14px; line-height: 1.5; color: #374151; }
      `}</style>
    </Page>
  );
}

// The saved snapshot is the built config; turn it back into editable fields.
function toFieldsFromSaved(json) {
  return toFields(JSON.parse(json));
}
